import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Building2, Loader2, Plus, ShieldCheck, X } from 'lucide-react';
import { api } from '../lib/api';

/* Client-side format validators — same rules as backend. Keeps the UX
   snappy (users see errors immediately) without leaking secrets. */
const PAN_RE = /^[A-Z]{5}[0-9]{4}[A-Z]$/;
const GSTIN_RE = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;
// Indian mobile: 10 digits, first digit 6-9. Country code (+91) is applied
// separately in the UI; the validated string here is just the local part.
const MOBILE_RE = /^[6-9][0-9]{9}$/;

const PAN_ENTITY_HINT = {
    P: 'Individual',
    C: 'Company',
    H: 'HUF',
    F: 'Firm / LLP',
    A: 'AOP',
    T: 'Trust',
    B: 'BOI',
    L: 'Local Authority',
    J: 'Artificial Judicial Person',
    G: 'Government',
};

const BUSINESS_TYPES = [
    { value: 'kirana', label: 'Kirana / Grocery' },
    { value: 'restaurant', label: 'Restaurant / Cafe' },
    { value: 'pharmacy', label: 'Pharmacy' },
    { value: 'wholesale', label: 'Wholesale / Distributor' },
    { value: 'services', label: 'Services / Agency' },
    { value: 'manufacturing', label: 'Manufacturing' },
    { value: 'other', label: 'Other' },
];

/**
 * Modal that captures / edits the caller's business identity (PAN + GSTINs).
 *
 * Props
 *  - open           : boolean
 *  - onOpenChange   : (bool) => void
 *  - initial        : optional workspace payload from GET /workspace
 *  - onSaved        : (workspace) => void   fired after successful save
 *  - dismissible    : boolean   false = block until PAN is set (first-run)
 */
export function BusinessSetupDialog({
    open,
    onOpenChange,
    initial,
    onSaved,
    dismissible = true,
}) {
    const [pan, setPan] = useState('');
    const [businessName, setBusinessName] = useState('');
    const [businessType, setBusinessType] = useState('');
    const [state, setState] = useState('');
    const [mobile, setMobile] = useState('');
    const [gstinsList, setGstinsList] = useState([]); // string[]
    const [gstinDraft, setGstinDraft] = useState('');
    const [saving, setSaving] = useState(false);
    const [errPan, setErrPan] = useState('');
    const [errGstin, setErrGstin] = useState('');
    const [errMobile, setErrMobile] = useState('');

    // Hydrate when the modal opens with existing data
    useEffect(() => {
        if (!open) return;
        setErrPan('');
        setErrGstin('');
        setErrMobile('');
        if (initial) {
            setPan((initial.pan || '').toUpperCase());
            setBusinessName(initial.business_name || '');
            setBusinessType(initial.business_type || '');
            setState(initial.state || '');
            // Server stores just the 10-digit local part; strip any prefix
            // for defensive normalization (older records may vary).
            setMobile((initial.mobile || '').replace(/\D/g, '').slice(-10));
            setGstinsList((initial.gstins || []).map((g) => (typeof g === 'string' ? g : g.gstin)));
        } else {
            setPan('');
            setBusinessName('');
            setBusinessType('');
            setState('');
            setMobile('');
            setGstinsList([]);
        }
        setGstinDraft('');
    }, [open, initial]);

    const panValid = PAN_RE.test(pan);
    const panEntityChar = panValid ? pan[3] : null;
    const panEntityLabel = panEntityChar ? PAN_ENTITY_HINT[panEntityChar] : null;
    const mobileValid = MOBILE_RE.test(mobile);

    const gstinDraftValid = GSTIN_RE.test(gstinDraft);
    const gstinDraftPan = gstinDraftValid ? gstinDraft.slice(2, 12) : '';
    const gstinLinksPan = gstinDraftValid && panValid && gstinDraftPan === pan;

    const canAddGstin = gstinDraftValid && gstinLinksPan && !gstinsList.includes(gstinDraft);

    const addGstinLocally = () => {
        setErrGstin('');
        const g = gstinDraft.trim().toUpperCase();
        if (!GSTIN_RE.test(g)) {
            setErrGstin('That GSTIN is not in the right format (15 chars: 2 state + 10 PAN + 3 tail).');
            return;
        }
        if (!panValid) {
            setErrGstin('Enter a valid PAN first.');
            return;
        }
        if (g.slice(2, 12) !== pan) {
            setErrGstin(`This GSTIN belongs to PAN "${g.slice(2, 12)}", which doesn't match your business PAN "${pan}".`);
            return;
        }
        if (gstinsList.includes(g)) {
            setErrGstin('This GSTIN is already in the list.');
            return;
        }
        setGstinsList((prev) => [...prev, g]);
        setGstinDraft('');
    };

    const save = async () => {
        setErrPan('');
        setErrMobile('');
        setErrGstin('');
        if (!panValid) {
            setErrPan('Please enter a valid PAN (10 chars: AAAAA9999A).');
            return;
        }
        if (!businessName.trim()) {
            setErrPan('Please enter your business name.');
            return;
        }
        if (!mobileValid) {
            setErrMobile('Please enter a valid 10-digit Indian mobile number (starting with 6, 7, 8, or 9).');
            return;
        }

        // Auto-commit a pending GSTIN draft — users often type a GSTIN and hit
        // "Save" without clicking the "Add" chip button. Silently swallowing
        // that draft would leave them with an empty GSTIN list and no clue
        // why (the classic "I saved and it's gone" bug). If the draft is
        // invalid we surface a hard error instead of dropping it silently.
        let finalGstins = gstinsList;
        const draft = gstinDraft.trim().toUpperCase();
        if (draft) {
            if (!GSTIN_RE.test(draft)) {
                setErrGstin('The GSTIN you typed is invalid (need 15 chars: 2 state + 10 PAN + 3 tail). Fix it or clear the field before saving.');
                return;
            }
            if (draft.slice(2, 12) !== pan) {
                setErrGstin(`This GSTIN belongs to PAN "${draft.slice(2, 12)}", which doesn't match your business PAN "${pan}". Fix it or clear the field before saving.`);
                return;
            }
            if (!gstinsList.includes(draft)) {
                finalGstins = [...gstinsList, draft];
                setGstinsList(finalGstins);
                setGstinDraft('');
            }
        }

        setSaving(true);
        try {
            const res = await api.saveWorkspace({
                pan,
                business_name: businessName.trim(),
                mobile,
                business_type: businessType || undefined,
                state: state.trim() || undefined,
                gstins: finalGstins,
            });
            toast.success(
                finalGstins.length
                    ? `Business profile saved · ${finalGstins.length} GSTIN${finalGstins.length === 1 ? '' : 's'}`
                    : 'Business profile saved',
            );
            onSaved?.(res.workspace);
            onOpenChange?.(false);
        } catch (e) {
            const msg = e?.response?.data?.detail || e.message || 'Failed to save';
            toast.error(msg);
            setErrPan(msg);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog
            open={open}
            onOpenChange={(v) => {
                if (!v && !dismissible) return; // block close on first-run
                onOpenChange?.(v);
            }}
        >
            <DialogContent
                data-testid="business-setup-dialog"
                className="max-w-lg max-h-[90vh] overflow-y-auto"
            >
                <DialogHeader>
                    <div className="flex items-center gap-2">
                        <div className="h-9 w-9 rounded-full bg-accent text-accent-foreground flex items-center justify-center">
                            <Building2 size={18} />
                        </div>
                        <DialogTitle className="font-heading">
                            {initial ? 'Business profile' : 'Set up your business'}
                        </DialogTitle>
                    </div>
                    <DialogDescription>
                        Anchoring bills to your PAN (and optional GSTINs) ensures every uploaded
                        bill belongs to <em>your</em> business. Required before uploading.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-2">
                    {/* PAN */}
                    <div className="space-y-1.5">
                        <Label htmlFor="pan-input" className="text-xs uppercase tracking-wide">
                            Business PAN <span className="text-destructive">*</span>
                        </Label>
                        <Input
                            id="pan-input"
                            data-testid="pan-input"
                            value={pan}
                            onChange={(e) => setPan(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 10))}
                            placeholder="AAAAA9999A"
                            className={`font-mono tracking-wider ${
                                pan.length === 10
                                    ? panValid
                                        ? 'border-[hsl(var(--success-border))]'
                                        : 'border-destructive'
                                    : ''
                            }`}
                            autoComplete="off"
                            autoCapitalize="characters"
                        />
                        {panValid && (
                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                <ShieldCheck size={12} className="text-[hsl(var(--success-fg))]" />
                                Valid PAN detected · {panEntityLabel || 'Unknown entity'}
                            </div>
                        )}
                        {pan.length === 10 && !panValid && (
                            <div className="text-xs text-destructive">
                                PAN format wrong. Expected: 5 letters + 4 digits + 1 letter (e.g. ABCDE1234F).
                            </div>
                        )}
                        {errPan && <div className="text-xs text-destructive">{errPan}</div>}
                    </div>

                    {/* Business name */}
                    <div className="space-y-1.5">
                        <Label htmlFor="biz-name-input" className="text-xs uppercase tracking-wide">
                            Business name <span className="text-destructive">*</span>
                        </Label>
                        <Input
                            id="biz-name-input"
                            data-testid="biz-name-input"
                            value={businessName}
                            onChange={(e) => setBusinessName(e.target.value.slice(0, 120))}
                            placeholder="Ravi Kirana Store"
                        />
                    </div>

                    {/* Mobile number (mandatory, India) */}
                    <div className="space-y-1.5">
                        <Label htmlFor="mobile-input" className="text-xs uppercase tracking-wide">
                            Mobile number <span className="text-destructive">*</span>
                        </Label>
                        <div className="flex gap-2">
                            <div
                                className="inline-flex items-center px-3 rounded-md border border-input bg-muted text-sm text-muted-foreground select-none"
                                aria-hidden="true"
                            >
                                +91
                            </div>
                            <Input
                                id="mobile-input"
                                data-testid="mobile-input"
                                type="tel"
                                inputMode="numeric"
                                autoComplete="tel-national"
                                value={mobile}
                                onChange={(e) => {
                                    // Strip everything except digits; enforce 10-digit cap.
                                    // If the user pastes a "+91"/"91"/"0" prefixed value, drop it.
                                    let d = e.target.value.replace(/\D/g, '');
                                    if (d.startsWith('91') && d.length > 10) d = d.slice(2);
                                    if (d.startsWith('0') && d.length > 10) d = d.slice(1);
                                    setMobile(d.slice(0, 10));
                                }}
                                placeholder="98765 43210"
                                className={`font-mono tracking-wider ${
                                    mobile.length === 10
                                        ? mobileValid
                                            ? 'border-[hsl(var(--success-border))]'
                                            : 'border-destructive'
                                        : ''
                                }`}
                                maxLength={10}
                            />
                        </div>
                        <div className="text-xs text-muted-foreground">
                            Used for account recovery &amp; important billing / OCR alerts. India only.
                        </div>
                        {mobile.length === 10 && !mobileValid && (
                            <div className="text-xs text-destructive">
                                Indian mobile numbers must start with 6, 7, 8, or 9.
                            </div>
                        )}
                        {errMobile && <div className="text-xs text-destructive">{errMobile}</div>}
                    </div>

                    {/* Business type + state (side-by-side) */}
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                            <Label htmlFor="biz-type-input" className="text-xs uppercase tracking-wide">
                                Business type
                            </Label>
                            <select
                                id="biz-type-input"
                                data-testid="biz-type-select"
                                value={businessType}
                                onChange={(e) => setBusinessType(e.target.value)}
                                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                            >
                                <option value="">Select…</option>
                                {BUSINESS_TYPES.map((t) => (
                                    <option key={t.value} value={t.value}>{t.label}</option>
                                ))}
                            </select>
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="state-input" className="text-xs uppercase tracking-wide">
                                State (optional)
                            </Label>
                            <Input
                                id="state-input"
                                data-testid="state-input"
                                value={state}
                                onChange={(e) => setState(e.target.value.slice(0, 60))}
                                placeholder="Maharashtra"
                            />
                        </div>
                    </div>

                    {/* GSTIN adder */}
                    <div className="space-y-1.5 pt-2 border-t border-border">
                        <Label className="text-xs uppercase tracking-wide">
                            GSTINs (optional — one per state)
                        </Label>
                        <div className="text-xs text-muted-foreground -mt-1">
                            No GSTIN? Skip this — Ease My Bill supports small businesses (under ₹40L
                            turnover) that operate on PAN only.
                        </div>
                        <div className="flex gap-2">
                            <Input
                                data-testid="gstin-input"
                                value={gstinDraft}
                                onChange={(e) =>
                                    setGstinDraft(
                                        e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 15),
                                    )
                                }
                                placeholder="27AAPFU0939F1ZV"
                                className="font-mono tracking-wide"
                                autoCapitalize="characters"
                            />
                            <Button
                                type="button"
                                variant="outline"
                                onClick={addGstinLocally}
                                disabled={!canAddGstin}
                                data-testid="add-gstin-btn"
                            >
                                <Plus size={14} className="mr-1" /> Add
                            </Button>
                        </div>
                        {errGstin && <div className="text-xs text-destructive">{errGstin}</div>}
                        {gstinDraft.length === 15 && !gstinDraftValid && (
                            <div className="text-xs text-destructive">
                                GSTIN format wrong (2 state + 10 PAN + 3 tail).
                            </div>
                        )}
                        {gstinDraftValid && panValid && !gstinLinksPan && (
                            <div className="text-xs text-destructive">
                                This GSTIN belongs to PAN <code>{gstinDraftPan}</code>, but your
                                workspace PAN is <code>{pan}</code>. A GSTIN's middle 10 characters
                                must equal your PAN.
                            </div>
                        )}
                        {gstinsList.length > 0 && (
                            <div className="flex flex-wrap gap-2 pt-2">
                                {gstinsList.map((g) => (
                                    <Badge
                                        key={g}
                                        variant="outline"
                                        className="font-mono text-[11px] gap-1 pr-1"
                                        data-testid={`gstin-chip-${g}`}
                                    >
                                        {g}
                                        <button
                                            type="button"
                                            onClick={() => setGstinsList((prev) => prev.filter((x) => x !== g))}
                                            className="hover:text-destructive"
                                            aria-label={`Remove ${g}`}
                                        >
                                            <X size={11} />
                                        </button>
                                    </Badge>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                <DialogFooter className="gap-2">
                    {dismissible && (
                        <Button
                            variant="ghost"
                            onClick={() => onOpenChange?.(false)}
                            disabled={saving}
                        >
                            Cancel
                        </Button>
                    )}
                    <Button
                        onClick={save}
                        disabled={saving || !panValid || !businessName.trim() || !mobileValid}
                        data-testid="save-workspace-btn"
                    >
                        {saving && <Loader2 size={14} className="mr-1 animate-spin" />}
                        {initial ? 'Update profile' : 'Save & continue'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
